/**
 * Sugar Cart Drawer
 * Main custom element for the cart drawer functionality
 */

class SugarCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.testMode = this.dataset.testMode === 'true';
    this.sectionId = this.dataset.sectionId;
    this.container = null;
    this.panel = null;
    this.cartUpdateRequestId = 0;
    this.openFinalizeTimeout = null;
    this.handleOpenTransitionEnd = null;

    // Bind methods
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onContainerClick = this.onContainerClick.bind(this);
  }

  connectedCallback() {
    this.refreshElementRefs();

    // Set up event listeners
    // Note: keydown listener is added/removed in open/close methods to avoid listening when closed
    // Use container click with event delegation for overlay clicks (more reliable than direct overlay listener)
    if (this.container) {
      this.container.removeEventListener('click', this.onContainerClick);
      this.container.addEventListener('click', this.onContainerClick);
    }

    // Set up cart icon click handler
    this.setHeaderCartIconAccessibility();

    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
    this.cartErrorUnsubscriber?.();
    this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError || 'cart-error', (event) => {
      if (!event?.commitUnknown || !this.isConnected) return;
      const errors = this.querySelector('#SugarCartDrawer-CartErrors');
      if (errors) errors.textContent = event.message;
    });

    // Initialize any enhancements inside the drawer (slider/countdown, etc.)
    initSugarCartDrawerEnhancements(this);

    // Test mode - keep drawer open
    if (this.testMode && Shopify.designMode) {
      this.testOpenTimer = setTimeout(() => { if (this.isConnected) this.open(); }, 100);
    }
  }

  disconnectedCallback() {
    this.headerCartController?.abort();
    this.cartErrorUnsubscriber?.();
    clearTimeout(this.testOpenTimer);
    destroySugarCartDrawerEnhancements(this);
    this.cartUpdateRequestId += 1;

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    if (this.container) {
      this.container.removeEventListener('click', this.onContainerClick);
    }

    if (this.openFinalizeTimeout) {
      clearTimeout(this.openFinalizeTimeout);
      this.openFinalizeTimeout = null;
    }

    if (this.handleOpenTransitionEnd) {
      this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
      this.handleOpenTransitionEnd = null;
    }

    // Clean up document-level listener if drawer was open when disconnected
    document.removeEventListener('keydown', this.onKeyUp);
  }

  setHeaderCartIconAccessibility() {
    this.headerCartController?.abort();
    this.headerCartController = new AbortController();
    const options = { signal: this.headerCartController.signal };
    const cartLink = document.querySelector('.header__icon--cart');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    }, options);

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    }, options);
  }

  onKeyUp(event) {
    if (event.code === 'Escape' && this.classList.contains('active')) {
      event.preventDefault();
      this.close();
    }
  }

  onContainerClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Handle close button clicks via delegation so button listeners never go stale.
    if (target.closest('.sugar-cart-drawer__close')) {
      event.preventDefault();
      this.close();
      return;
    }

    // Close drawer when clicking outside the panel.
    const currentPanel = this.querySelector('.sugar-cart-drawer__panel');
    if (!currentPanel || !currentPanel.contains(target)) {
      this.close();
    }
  }

  open(triggeredBy) {
    this.refreshElementRefs();

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    // Prevent body scroll immediately even if the drawer is already open.
    document.body.classList.add('sugar-cart-drawer-open', 'overflow-hidden');

    // Keep open idempotent when multiple add-to-cart handlers call it.
    if (this.classList.contains('active')) {
      refreshSugarCartAnnouncements(this);
      return;
    }

    // Add document-level keyboard listener for Escape key
    document.removeEventListener('keydown', this.onKeyUp);
    document.addEventListener('keydown', this.onKeyUp);

    if (this.openFinalizeTimeout) {
      clearTimeout(this.openFinalizeTimeout);
      this.openFinalizeTimeout = null;
    }

    if (this.handleOpenTransitionEnd) {
      this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
      this.handleOpenTransitionEnd = null;
    }

    const finalizeOpen = () => {
      if (!this.classList.contains('active')) return;

      // Ensure sliders/layout measure correctly once the drawer is visible
      refreshSugarCartAnnouncements(this);

      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.sugar-cart-drawer__empty')
        : this.querySelector('#SugarCartDrawer');

      const focusElement = this.querySelector('.sugar-cart-drawer__panel') ||
                          this.querySelector('.sugar-cart-drawer__close');

      if (typeof trapFocus === 'function') {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    };

    const currentPanel = this.panel || this.querySelector('.sugar-cart-drawer__panel');
    if (currentPanel) {
      this.handleOpenTransitionEnd = (transitionEvent) => {
        if (transitionEvent.target !== currentPanel) return;

        finalizeOpen();

        if (this.openFinalizeTimeout) {
          clearTimeout(this.openFinalizeTimeout);
          this.openFinalizeTimeout = null;
        }

        if (this.handleOpenTransitionEnd) {
          this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
          this.handleOpenTransitionEnd = null;
        }
      };

      this.addEventListener('transitionend', this.handleOpenTransitionEnd);
    }

    // Fallback when transitionend is skipped due rapid DOM/state changes.
    this.openFinalizeTimeout = setTimeout(() => {
      finalizeOpen();
      this.openFinalizeTimeout = null;

      if (this.handleOpenTransitionEnd) {
        this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
        this.handleOpenTransitionEnd = null;
      }
    }, 380);

    // Trigger animation
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
        // Initialize slider early while panel slides in (element is visible and measurable now)
        requestAnimationFrame(() => refreshSugarCartAnnouncements(this));
      });
    } else {
      setTimeout(() => {
        this.classList.add('animate', 'active');
        setTimeout(() => refreshSugarCartAnnouncements(this));
      });
    }
  }

  close() {
    if (this.openFinalizeTimeout) {
      clearTimeout(this.openFinalizeTimeout);
      this.openFinalizeTimeout = null;
    }

    if (this.handleOpenTransitionEnd) {
      this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
      this.handleOpenTransitionEnd = null;
    }

    this.classList.remove('active');

    // Remove document-level keyboard listener
    document.removeEventListener('keydown', this.onKeyUp);

    // Restore focus
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    // Restore body scroll
    document.body.classList.remove('sugar-cart-drawer-open', 'overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshElementRefs() {
    this.container = this.querySelector('#SugarCartDrawer');
    this.panel = this.querySelector('.sugar-cart-drawer__panel');
  }

  /**
   * Render updated cart contents from server response
   * @param {Object} parsedState - Response from cart API containing sections
   */
  renderContents(parsedState, event = {}) {
    if (!this.isConnected) return;
    if (event.revision && event.revision <= (this.renderedRevision || 0)) return;
    if (event.revision) this.renderedRevision = event.revision;
    const sectionId = this.sectionId || 'sugar-cart-drawer';
    const html = parsedState.sections?.[sectionId];
    if (typeof html === 'string' && html.trim()) {
      const source = new DOMParser().parseFromString(html, 'text/html');
      const target = this.querySelector('.sugar-cart-drawer__panel');
      if (target && source.querySelector('.sugar-cart-drawer__panel')) {
        destroySugarCartDrawerEnhancements(this);
        window.Alpine?.destroyTree?.(target);
        target.innerHTML = this.getSectionInnerHTML(source, '.sugar-cart-drawer__panel');
        window.Alpine?.initTree?.(target);
        this.refreshElementRefs();
        initSugarCartDrawerEnhancements(this);
        if (this.classList.contains('active')) initSugarCartAnnouncements(this);
      }
      const sourceDrawer = source.querySelector('sugar-cart-drawer');
      if (sourceDrawer) this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    }
    if (Number.isFinite(parsedState.item_count)) this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.refreshCartBubble(parsedState.sections);
    if (event.open) this.open();
    if (event.warnings?.length) {
      const errors = this.querySelector('#SugarCartDrawer-CartErrors');
      if (errors) errors.textContent = window.cartStrings?.refreshError || 'Cart saved. Reload the cart to check its latest state.';
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    // Reuse this render's parsed section; retain string input for existing callers.
    const doc = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
    const element = doc.querySelector(selector);
    if (!element) return '';

    // Extract style tags and append them to document head to ensure they're processed
    const styleTags = element.querySelectorAll('style');
    styleTags.forEach((style, index) => {
      // Keep the existing positional style identifiers for compatibility.
      const styleId = `sugar-cart-style-${index}`;

      const existingStyle = document.head.querySelector(`style[data-cart-drawer-style="${styleId}"]`);
      if (existingStyle) {
        // Preserve cascade order even when only one block's CSS changes.
        if (existingStyle.textContent !== style.textContent) existingStyle.textContent = style.textContent;
      } else {
        const newStyle = document.createElement('style');
        newStyle.textContent = style.textContent;
        newStyle.setAttribute('data-cart-drawer-style', styleId);
        document.head.appendChild(newStyle);
      }
      style.remove();
    });

    return element.innerHTML;
  }

  refreshCartBubble(sections) {
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    var html = sections && sections['cart-icon-bubble'];
    if (typeof html === 'string') {
      var doc = new DOMParser().parseFromString(html || '<div></div>', 'text/html');
      var bubble = doc.querySelector('.cart-count-bubble');
      target.innerHTML = bubble ? bubble.outerHTML : '';
      return;
    }


  }

  getSectionsToRender() {
    return [
      {
        id: 'sugar-cart-drawer',
        selector: '#SugarCartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  /**
   * Handle cart update events from PubSub
   */
  onCartUpdate(event) {
    if (!event) return;
    this.renderContents({ ...(event.cartData || {}), sections: event.sections || event.cartData?.sections }, event);
  }
}

customElements.define('sugar-cart-drawer', SugarCartDrawer);

function destroySugarCartDrawerEnhancements(root) {
  root.announcementGeneration = (root.announcementGeneration || 0) + 1;
  root.announcementDependencyPromise = null;
  clearTimeout(root.announcementRetry);
  clearInterval(root.countdownInterval);
  clearInterval(root.sophisticatedCountdownInterval);
  root.querySelectorAll('[data-sugar-cart-announcements-splide]').forEach((element) => {
    element._splideInstance?.destroy?.(true);
    element._splideInstance = null;
    element._splideInitializing = false;
  });
}

function initSugarCartDrawerEnhancements(rootEl) {
  if (!rootEl || !(rootEl instanceof HTMLElement)) return;
  // Don't initialize announcements slider here - wait until drawer is visible
  initSugarCartDrawerCountdownTimers(rootEl);
  initSugarCartSophisticatedCountdowns(rootEl);
}

function refreshSugarCartAnnouncements(rootEl) {
  if (!rootEl || !(rootEl instanceof HTMLElement) || !rootEl.isConnected) return;

  const containers = rootEl.querySelectorAll('[data-sugar-cart-announcements]');
  if (!containers.length) return;

  containers.forEach((container) => {
    const splideEl = container.querySelector('[data-sugar-cart-announcements-splide]');
    if (!splideEl) return;

    // If already initialized, do nothing (don't refresh - it causes jitter)
    if (splideEl._splideInstance || splideEl._splideInitializing) {
      return;
    }

    // Mark as initializing to prevent duplicate attempts
    splideEl._splideInitializing = true;

    // Initialize for the first time (drawer is now visible)
    initSugarCartAnnouncements(rootEl);
  });
}

function initSugarCartAnnouncements(rootEl, attempt = 0) {
  if (!rootEl.isConnected) return;
  const containers = rootEl.querySelectorAll('[data-sugar-cart-announcements]');
  if (!containers.length) return;

  containers.forEach((container) => {
    const enableSlider = container.dataset.enableSlider === 'true';
    const splideEl = container.querySelector('[data-sugar-cart-announcements-splide]');
    if (!enableSlider || !splideEl) return;

    // Skip if already initialized
    if (splideEl._splideInstance) return;

    // Wait for Splide to load (it may be loaded after this file due to defer ordering)
    if (typeof Splide === 'undefined') {
      const runtime = window.SugarTheme?.runtime;
      if (runtime?.load) {
        if (!rootEl.announcementDependencyPromise) {
          const generation = rootEl.announcementGeneration || 0;
          const pending = Promise.resolve(runtime.load('splide')).then(() => {
            if (!rootEl.isConnected || generation !== (rootEl.announcementGeneration || 0)) return;
            if (typeof Splide === 'undefined') throw new Error('Splide unavailable after runtime load');
            initSugarCartAnnouncements(rootEl);
          }).catch((error) => {
            if (generation !== (rootEl.announcementGeneration || 0)) return;
            rootEl.querySelectorAll('[data-sugar-cart-announcements-splide]').forEach((node) => { node._splideInitializing = false; });
            document.dispatchEvent(new CustomEvent('sugar:runtime-error', { detail: { error, root: rootEl } }));
          }).finally(() => {
            if (rootEl.announcementDependencyPromise === pending) rootEl.announcementDependencyPromise = null;
          });
          rootEl.announcementDependencyPromise = pending;
        }
      } else if (attempt < 40) {
        clearTimeout(rootEl.announcementRetry);
        rootEl.announcementRetry = setTimeout(() => initSugarCartAnnouncements(rootEl, attempt + 1), 50);
      } else {
        splideEl._splideInitializing = false;
      }
      return;
    }

    // Ensure required classes exist for Splide.
    // Only tag <li> children as slides — integrity guards inject sibling
    // <style>/<script> nodes which Splide would otherwise render as visible
    // "slides", surfacing their source text inside the cart drawer.
    const listEl = splideEl.querySelector('.splide__list');
    if (listEl) {
      Array.from(listEl.children).forEach((child) => {
        if (child && child.tagName === 'LI' && child.classList) {
          child.classList.add('splide__slide');
        }
      });
    }

    const slideCount = parseInt(container.dataset.slideCount || '0', 10) || 0;
    const autoplay = container.dataset.autoplay === 'true';
    const autoplaySpeed = (parseInt(container.dataset.autoplaySpeed || '5', 10) || 5) * 1000;
    const pauseOnHover = container.dataset.pauseOnHover === 'true';
    const showArrows = container.dataset.showArrows === 'true';
    const arrowColorMode = container.dataset.arrowColor || 'auto';

    const options = {
      type: 'loop',
      perPage: 1,
      perMove: 1,
      pagination: false,
      arrows: showArrows && slideCount > 1,
      autoplay: autoplay && slideCount > 1,
      interval: autoplaySpeed,
      pauseOnHover: pauseOnHover,
      pauseOnFocus: true,
      drag: slideCount > 1,
      speed: 400,
      easing: 'ease',
      gap: 0,
      width: '100%',
      fixedWidth: false,
      autoWidth: false
    };

    const splide = new Splide(splideEl, options);

    function updateArrowColor() {
      if (arrowColorMode !== 'auto') return;

      const slideObj = splide.Components?.Slides?.getAt(splide.index);
      const activeSlide = slideObj?.slide || container.querySelector('.splide__slide.is-active');
      const textColor = activeSlide?.dataset?.textColor;

      if (textColor) {
        container.style.setProperty('--sugar-cart-announcements-arrow-color', textColor);
      }
    }

    const trackEl = splideEl.querySelector('.splide__track');

    function updateTrackBackground(nextIndex) {
      if (!trackEl) return;

      const index = typeof nextIndex === 'number' ? nextIndex : splide.index;
      const slideObj = splide.Components?.Slides?.getAt(index);
      const slide = slideObj?.slide;
      // Support both announcement items and countdown slides
      const inner = slide?.querySelector('.sugar-cart-announcement-item__inner') ||
                    slide?.querySelector('.sugar-cart-announcement-countdown__inner') ||
                    slide?.querySelector('.sugar-cart-announcement-rotating-text__inner');

      if (inner) {
        const bg = getComputedStyle(inner).backgroundColor;
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
          trackEl.style.backgroundColor = bg;
        }
      }
    }

    splide.on('mounted', () => updateTrackBackground());
    splide.on('move', (newIndex) => updateTrackBackground(newIndex));

    if (arrowColorMode === 'auto') {
      splide.on('mounted', updateArrowColor);
      splide.on('move', updateArrowColor);
      splide.on('updated', updateArrowColor);
    }

    splide.mount();
    splideEl._splideInstance = splide;

    // Refresh after a short delay to ensure proper loop initialization
    requestAnimationFrame(() => {
      if (splideEl._splideInstance) {
        splideEl._splideInstance.refresh();
      }
    });
  });
}

function initSugarCartDrawerCountdownTimers(rootEl) {
  // One interval for the entire cart drawer to avoid leaks on re-render.
  if (rootEl.countdownInterval) {
    clearInterval(rootEl.countdownInterval);
    rootEl.countdownInterval = null;
  }

  const timers = Array.from(rootEl.querySelectorAll('[data-countdown-timer]'));
  if (!timers.length) return;

  const timerStates = timers.map((timer) => {
    const hoursEl = timer.querySelector('[data-countdown-hours]');
    const minutesEl = timer.querySelector('[data-countdown-minutes]');
    const secondsEl = timer.querySelector('[data-countdown-seconds]');

    const initialHours = parseInt(timer.dataset.initialHours, 10) || 0;
    const initialMinutes = parseInt(timer.dataset.initialMinutes, 10) || 0;
    const initialSeconds = parseInt(timer.dataset.initialSeconds, 10) || 0;
    const shouldRestart = timer.dataset.restart === 'restart';

    let totalSeconds = (initialHours * 3600) + (initialMinutes * 60) + initialSeconds;
    const originalTotal = totalSeconds;

    function updateDisplay() {
      const hrs = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      if (hoursEl) hoursEl.textContent = String(hrs).padStart(2, '0');
      if (minutesEl) minutesEl.textContent = String(mins).padStart(2, '0');
      if (secondsEl) secondsEl.textContent = String(secs).padStart(2, '0');
    }

    updateDisplay();

    return {
      tick: () => {
        if (totalSeconds > 0) {
          totalSeconds -= 1;
          updateDisplay();
          return;
        }
        if (shouldRestart) {
          totalSeconds = originalTotal;
          updateDisplay();
        }
      }
    };
  });

  rootEl.countdownInterval = setInterval(() => {
    timerStates.forEach((state) => state.tick());
  }, 1000);
}

function initSugarCartSophisticatedCountdowns(rootEl) {
  // Clear any existing interval for sophisticated countdowns
  if (rootEl.sophisticatedCountdownInterval) {
    clearInterval(rootEl.sophisticatedCountdownInterval);
    rootEl.sophisticatedCountdownInterval = null;
  }

  // Pin the host element so each interval tick re-queries from the current cart drawer DOM.
  // After add-to-cart, #SugarCartDrawer.innerHTML is swapped wholesale; capturing element
  // references at init time leaves us writing into detached <span>s while the visible spans
  // keep the SSR "00" default. Re-querying on every tick is cheap and keeps the timer in
  // sync regardless of how many times the drawer's contents are replaced.
  const host = rootEl || document.querySelector('sugar-cart-drawer') || document;

  function pad(num) {
    return String(num).padStart(2, '0');
  }

  function parseDateParts(str) {
    if (!str) return null;
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return null;
    return { y: +m[1], mo: +m[2] - 1, d: +m[3], h: +m[4], mi: +m[5], s: +(m[6] || 0) };
  }

  // Per-timer state keyed by the element itself so auto_renew end time persists
  // across ticks even though we don't keep the timer node in a closure.
  const stateByTimer = new WeakMap();

  function getState(timer) {
    let state = stateByTimer.get(timer);
    if (!state) {
      state = { autoRenewEndTime: null };
      stateByTimer.set(timer, state);
    }
    return state;
  }

  function getTargetDate(timer) {
    const state = getState(timer);
    const type = timer.dataset.countdownType;
    const timezoneMode = timer.dataset.timezoneMode;
    const utcOffset = parseInt(timer.dataset.utcOffset, 10) || 0;

    if (type === 'specific_date') {
      const p = parseDateParts(timer.dataset.endDate);
      if (!p) return null;

      let targetDate;
      if (timezoneMode === 'fixed') {
        targetDate = new Date(Date.UTC(p.y, p.mo, p.d, p.h, p.mi, p.s) - utcOffset * 3600000);
      } else {
        targetDate = new Date(p.y, p.mo, p.d, p.h, p.mi, p.s);
      }
      if (isNaN(targetDate.getTime())) return null;
      return targetDate;
    }

    if (type === 'every_24_hours') {
      const now = new Date();
      let midnight;

      if (timezoneMode === 'fixed') {
        const utcMidnight = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0)
        );
        midnight = new Date(utcMidnight.getTime() - utcOffset * 60 * 60 * 1000);
      } else {
        midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      }
      return midnight;
    }

    if (type === 'auto_renew') {
      if (!state.autoRenewEndTime) {
        const autoDays = parseInt(timer.dataset.autoDays, 10) || 0;
        const autoHours = parseInt(timer.dataset.autoHours, 10) || 0;
        const autoMinutes = parseInt(timer.dataset.autoMinutes, 10) || 0;
        const autoSeconds = parseInt(timer.dataset.autoSeconds, 10) || 0;
        const totalMs =
          autoDays * 24 * 60 * 60 * 1000 +
          autoHours * 60 * 60 * 1000 +
          autoMinutes * 60 * 1000 +
          autoSeconds * 1000;
        state.autoRenewEndTime = new Date(Date.now() + totalMs);
      }
      return state.autoRenewEndTime;
    }

    return null;
  }

  function updateDisplay(timer, days, hours, minutes, seconds) {
    const daysEl = timer.querySelector('[data-days]');
    const hoursEl = timer.querySelector('[data-hours]');
    const minutesEl = timer.querySelector('[data-minutes]');
    const secondsEl = timer.querySelector('[data-seconds]');
    if (daysEl) daysEl.textContent = pad(days);
    if (hoursEl) hoursEl.textContent = pad(hours);
    if (minutesEl) minutesEl.textContent = pad(minutes);
    if (secondsEl) secondsEl.textContent = pad(seconds);
  }

  function tickOne(timer) {
    // Skip detached timers — happens briefly while #SugarCartDrawer.innerHTML is being swapped.
    if (!timer.isConnected) return;

    const type = timer.dataset.countdownType;
    const target = getTargetDate(timer);
    if (!target) {
      updateDisplay(timer, 0, 0, 0, 0);
      return;
    }

    const now = new Date();
    let diff = target - now;

    if (diff <= 0) {
      if (type === 'specific_date') {
        timer.classList.add('countdown-timer--ended');
        return;
      } else if (type === 'every_24_hours' || type === 'auto_renew') {
        getState(timer).autoRenewEndTime = null;
        tickOne(timer);
        return;
      }
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    diff -= days * 1000 * 60 * 60 * 24;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    diff -= hours * 1000 * 60 * 60;
    const minutes = Math.floor(diff / (1000 * 60));
    diff -= minutes * 1000 * 60;
    const seconds = Math.floor(diff / 1000);

    updateDisplay(timer, days, hours, minutes, seconds);
  }

  function tickAll() {
    const timers = host.querySelectorAll
      ? host.querySelectorAll('[data-sophisticated-countdown]')
      : [];
    if (!timers.length) return;
    timers.forEach(tickOne);
  }

  // Run immediately so the visible spans flip from "00" to their real values within the
  // same frame the drawer slides in (not on the next 1-second tick).
  tickAll();

  rootEl.sophisticatedCountdownInterval = setInterval(tickAll, 1000);
}


/**
 * Sugar Cart Items
 * Handles cart item quantity updates and removals
 */
class SugarCartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('SugarCartDrawer-LineItemStatus');
    this.sectionId = this.dataset.sectionId;

    this.onInputChange = (event) => {
      clearTimeout(this.changeTimer);
      this.changeTimer = setTimeout(() => {
        if (this.isConnected) this.onChange(event);
      }, ON_CHANGE_DEBOUNCE_TIMER);
    };
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.addEventListener('change', this.onInputChange);
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.setupRemoveButtons();
      this.setupQuantityButtons();
    });
  }

  disconnectedCallback() {
    clearTimeout(this.changeTimer);
    this.removeEventListener('change', this.onInputChange);
  }

  setupRemoveButtons() {
    const buttons = this.querySelectorAll('.sugar-cart-item__remove');
    buttons.forEach((button) => {
      if (button.dataset.sugarCartRemoveBound === 'true') return;
      button.dataset.sugarCartRemoveBound = 'true';

      button.addEventListener('click', (event) => {
        event.preventDefault();
        const index = button.dataset.index;
        const key = button.dataset.key;
        this.updateQuantity(index, 0, event, undefined, undefined, key);
      });
    });
  }

  setupQuantityButtons() {
    const buttons = this.querySelectorAll('.sugar-cart-item__quantity-btn');
    buttons.forEach((button) => {
      if (button.dataset.sugarCartQuantityBound === 'true') return;
      button.dataset.sugarCartQuantityBound = 'true';

      button.addEventListener('click', (event) => {
        event.preventDefault();
        const input = button.closest('.sugar-cart-item__quantity').querySelector('.sugar-cart-item__quantity-input');
        if (!input) return;

        const currentValue = parseInt(input.value, 10);
        const step = parseInt(input.step, 10) || 1;
        const min = parseInt(input.min, 10) || 0;
        const max = input.max ? parseInt(input.max, 10) : Infinity;

        let newValue;
        if (button.name === 'plus') {
          newValue = Math.min(currentValue + step, max);
        } else {
          newValue = Math.max(currentValue - step, min);
        }

        if (newValue !== currentValue) {
          input.value = newValue;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  onChange(event) {
    const input = event.target;
    if (!input.classList.contains('sugar-cart-item__quantity-input')) return;

    const index = input.dataset.index;
    const key = input.dataset.key;
    const value = parseInt(input.value, 10);

    this.updateQuantity(index, value, event, input.name, input.dataset.quantityVariantId, key);
  }

  async updateQuantity(line, quantity, event, name, variantId, lineItemKey) {
    if (!this.isConnected) return;
    this.enableLoading(line);
    this.clearLiveRegions();
    try {
      const payload = { quantity };
      if (lineItemKey) payload.id = lineItemKey;
      else payload.line = Number(line);
      await window.SugarTheme.cart.request('change', payload, { source: 'sugar-cart-items' });
    } catch (error) {
      if (!this.isConnected) return;
      const input = this.querySelector(`[data-index="${line}"]`);
      if (input) input.value = input.getAttribute('value');
      this.updateLiveRegions(line, error.message || window.cartStrings?.error);
    } finally {
      this.disableLoading(line);
    }
  }

  clearLiveRegions() {
    document.querySelectorAll('.sugar-cart-item__error').forEach((lineItemError) => {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.textContent = '';
      }
      lineItemError.classList.remove('is-active');
    });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`SugarCartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.textContent = message;
      }
      lineItemError.classList.toggle('is-active', Boolean(message));
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'true');
    }

    const cartStatus = document.getElementById('SugarCartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', 'false');
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  enableLoading(line) {
    const item = this.querySelector(`[data-line-index="${line}"]`);
    if (item) {
      item.classList.add('is-loading');
    }

    const cartItems = document.getElementById('SugarCartDrawer-CartItems');
    if (cartItems) {
      cartItems.classList.add('cart__items--disabled');
    }

    document.activeElement?.blur();

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'false');
    }
  }

  disableLoading(line) {
    const item = this.querySelector(`[data-line-index="${line}"]`);
    if (item) {
      item.classList.remove('is-loading');
    }

    const cartItems = document.getElementById('SugarCartDrawer-CartItems');
    if (cartItems) {
      cartItems.classList.remove('cart__items--disabled');
    }
  }
}

customElements.define('sugar-cart-items', SugarCartItems);


/**
 * Sugar Cart Progress
 * Handles the progress bar with live updates
 */
class SugarCartProgress extends HTMLElement {
  constructor() {
    super();
    this.goals = [];
    this.lifecycleVersion = 0;
    this.parseGoals();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.updateProgress(event.cartData, event.source);
    });

    // Sync initial bar widths from live cart data so mode toggles apply immediately.
    this.initialProgressSync();
  }

  async initialProgressSync() {
    const lifecycleVersion = this.lifecycleVersion;
    const revision = window.SugarTheme.cart.revision;
    try {
      const cartData = await this.fetchCart();
      if (!this.isConnected || lifecycleVersion !== this.lifecycleVersion || revision !== window.SugarTheme.cart.revision) return;
      this.updateProgress(cartData, 'initial-sync');
    } catch (e) {
      // Silent fail on initial sync
    }
  }

  disconnectedCallback() {
    this.lifecycleVersion += 1;
    this.progressRequestId = (this.progressRequestId || 0) + 1;
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  parseGoals() {
    // Parse goal data from data attributes
    // Note: data-goal-1-target becomes dataset['goal-1Target'] (hyphen before number stays)
    const goalCount = parseInt(this.dataset.goalCount, 10) || 1;
    this.fillGoalsIndependently = this.dataset.fillGoalsIndependently === 'true';
    this.autoAddProperty = '_sugar_progress_goal';
    this.processing = false;

    for (let i = 1; i <= goalCount; i++) {
      const type = this.dataset[`goal-${i}Type`];
      const price = parseFloat(this.dataset[`goal-${i}Price`]) || 0;
      const quantity = parseInt(this.dataset[`goal-${i}Quantity`], 10) || 0;
      const variantId = this.dataset[`goal-${i}Variant`];
      const autocompleteSub = this.dataset[`goal-${i}AutocompleteSub`] === 'true';
      const target = parseInt(this.dataset[`goal-${i}Target`], 10) || 0;

      this.goals.push({
        type,
        threshold: target || (type === 'price' ? price * 100 : quantity),
        target: target || (type === 'price' ? price * 100 : quantity),
        reached: false,
        variantId: variantId ? parseInt(variantId, 10) : null,
        autocompleteSub,
        goalNumber: i
      });
    }
  }

  async updateProgress(cartData, source) {
    if (!cartData) return;
    const requestId = this.progressRequestId = (this.progressRequestId || 0) + 1;
    // /cart/add returns added lines, not cart totals or the existing cart items.
    if (!Array.isArray(cartData.items) || !Number.isFinite(cartData.total_price) || !Number.isFinite(cartData.item_count)) {
      try { cartData = await this.fetchCart(); } catch (error) { return; }
      if (requestId !== this.progressRequestId) return;
    }

    const cartTotal = cartData.total_price;
    const cartItemCount = cartData.item_count;
    const hasSubscription = Array.isArray(cartData.items)
      ? cartData.items.some((item) => item.selling_plan_allocation)
      : false;
    const fillGoalsIndependently = (this.dataset.fillGoalsIndependently || '')
      .toString()
      .trim()
      .toLowerCase() === 'true';

    const hasHigherAutocomplete = (goalNum) => {
      return this.goals.some((g) => g.goalNumber > goalNum && g.autocompleteSub && hasSubscription);
    };

    const isAutocompleteReached = (goal) => {
      return hasSubscription && (goal.autocompleteSub || hasHigherAutocomplete(goal.goalNumber));
    };

    // Calculate reached state first so sequential mode can gate later goals.
    this.goals.forEach((goal, index) => {
      const currentValue = goal.type === 'price' ? cartTotal : cartItemCount;
      goal.reached = currentValue >= goal.threshold || isAutocompleteReached(goal);
    });

    // Calculate progress for each goal
    this.goals.forEach((goal, index) => {
      const currentValue = goal.type === 'price' ? cartTotal : cartItemCount;
      const goalAutocompleteReached = isAutocompleteReached(goal);
      let progress = 0;

      if (goalAutocompleteReached) {
        progress = 100;
      } else if (index === 0) {
        progress = goal.threshold > 0 ? (currentValue / goal.threshold) * 100 : 100;
      } else if (fillGoalsIndependently) {
        progress = goal.threshold > 0 ? (currentValue / goal.threshold) * 100 : 100;
      } else {
        const previousGoal = this.goals[index - 1];
        if (!previousGoal.reached) {
          progress = 0;
        } else {
          const segmentStart = goal.type === previousGoal.type ? previousGoal.threshold : 0;
          if (currentValue <= segmentStart) {
            progress = 0;
          } else if (currentValue >= goal.threshold) {
            progress = 100;
          } else {
            const segmentRange = goal.threshold - segmentStart;
            progress = segmentRange > 0
              ? ((currentValue - segmentStart) / segmentRange) * 100
              : 100;
          }
        }
      }

      if (!Number.isFinite(progress)) progress = 0;
      progress = Math.max(0, Math.min(progress, 100));

      // Update progress bar UI
      const bar = this.querySelector(`.sugar-cart-progress__bar[data-goal="${index + 1}"], [data-goal="${index + 1}"]`);
      if (bar) {
        bar.style.width = `${progress}%`;
      }

      // Update milestone state
      const milestone = this.querySelector(`.sugar-cart-progress__milestone[data-goal="${index + 1}"]`);
      if (milestone) {
        milestone.classList.toggle('sugar-cart-progress__milestone--reached', goal.reached);
      }
    });

    // Update text with placeholders
    this.updateProgressText(cartData);

    // Handle auto-add/remove products

  }

  updateProgressText(cartData) {
    const textElement = this.querySelector('.sugar-cart-progress__text');
    if (!textElement) return;

    // Find the first unreached goal
    const currentGoal = this.goals.find(g => !g.reached) || this.goals[this.goals.length - 1];
    if (!currentGoal) return;

    const goalIndex = this.goals.indexOf(currentGoal);

    // Calculate remaining
    const currentValue = currentGoal.type === 'price'
      ? cartData.total_price
      : cartData.item_count;

    const remaining = Math.max(0, currentGoal.threshold - currentValue);

    // Format remaining amount
    let remainingText;
    if (currentGoal.type === 'price') {
      remainingText = window.SugarTheme.money.format(remaining);
    } else {
      remainingText = remaining.toString();
    }

    // Update placeholder text (handled server-side in Liquid)
    // This is for client-side updates after cart changes
  }

  async fetchCart() {
    return window.SugarTheme.cart.state || window.SugarTheme.cart.read();
  }
}

customElements.define('sugar-cart-progress', SugarCartProgress);
